import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Star } from 'lucide-react';
import Header from '../components/Header';
import SEOHead, { breadcrumbSchema } from '../components/SEOHead';
import { Reveal, Stagger, StaggerItem } from '../components/motion/Reveal';
import { Magnetic } from '../components/motion/Magnetic';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const trackVendorClick = (vendorId, target = 'website') => {
  fetch(`${API_URL}/api/vendors/${vendorId}/click`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ target }), keepalive: true }).catch(() => {});
};

export const VendorCard = ({ vendor }) => (
  <Link to={`/vendors/${vendor.slug}`} className="group block" data-testid={`vendor-card-${vendor.slug}`}>
    <div className="aspect-[4/3] overflow-hidden bg-[#f0f0f0] relative">
      {vendor.image_url ? (
        <img src={vendor.image_url} alt={`${vendor.name} — ${vendor.categories?.join(', ')}`} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]" />
      ) : (
        <div className="w-full h-full flex items-center justify-center font-presti text-3xl text-black/20">{vendor.name?.[0]}</div>
      )}
      {vendor.is_featured && <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-1 bg-white/90 backdrop-blur text-[9px] tracking-[0.25em] uppercase"><Star size={9} fill="currentColor" /> Trusted</span>}
    </div>
    <p className="text-[9px] tracking-[0.3em] uppercase text-black/40 mt-4">{vendor.categories?.slice(0, 2).join(' · ')}</p>
    <h3 className="font-presti text-xl md:text-2xl text-black/90 mt-1 group-hover:text-black">{vendor.name}</h3>
    {vendor.short_description && <p className="text-sm text-black/50 mt-2 line-clamp-2">{vendor.short_description}</p>}
    <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-black/50 mt-3 group-hover:text-black">View <ArrowRight size={11} /></span>
  </Link>
);

export default function VendorsPage() {
  const [vendors, setVendors] = useState([]);
  const [settings, setSettings] = useState({});
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/api/vendors`).then((r) => r.json()).catch(() => []),
      fetch(`${API_URL}/api/content/settings`).then((r) => r.json()).catch(() => ({})),
    ]).then(([v, s]) => { setVendors(Array.isArray(v) ? v : []); setSettings(s || {}); }).finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => [...new Set(vendors.flatMap((v) => v.categories || []))], [vendors]);
  const shown = category ? vendors.filter((v) => v.categories?.includes(category)) : vendors;

  return (
    <main className="bg-white min-h-screen text-black" data-testid="vendors-page">
      <SEOHead
        title="Trusted Wedding Vendors"
        description="Planners, venues, decor, makeup artists and more that Crystal Media has worked alongside at luxury South Asian and multicultural weddings across Toronto, Canada & USA."
        url="/vendors"
        keywords={['Toronto wedding vendors', 'South Asian wedding planner Toronto', 'wedding decor Toronto', 'bridal makeup artist Toronto']}
        siteSettings={settings}
        jsonLd={[breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Vendors', path: '/vendors' }])]}
      />
      <Header logoUrl={settings.photo_logo_url || settings.logo_url || ''} variant="light" />

      <section className="pt-36 md:pt-44 pb-12 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <Reveal><p className="text-[10px] tracking-[0.4em] uppercase text-black/40 mb-6">The People We Trust</p></Reveal>
          <Reveal delay={0.08}><h1 className="font-presti text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl" data-testid="vendors-heading">Vendors we&rsquo;d recommend to our own family</h1></Reveal>
          <Reveal delay={0.16}><p className="text-black/55 text-base md:text-lg leading-relaxed max-w-2xl mt-8">After hundreds of weddings you learn who shows up early, who stays calm at 2 a.m., and whose work makes ours look even better. These are those people.</p></Reveal>
          {categories.length > 1 && (
            <Reveal delay={0.22} className="flex flex-wrap gap-2 mt-10" data-testid="vendor-filters">
              {['', ...categories].map((c) => (
                <button key={c || 'all'} type="button" onClick={() => setCategory(c)} data-testid={`vendor-filter-${c ? c.toLowerCase().replace(/[^a-z]+/g, '-') : 'all'}`}
                  className={`px-4 py-2 text-[10px] tracking-[0.25em] uppercase border transition-colors ${category === c ? 'bg-black text-white border-black' : 'border-black/15 text-black/60 hover:border-black hover:text-black'}`}>
                  {c || 'All'}
                </button>
              ))}
            </Reveal>
          )}
        </div>
      </section>

      <section className="px-6 md:px-12 pb-24">
        <div className="max-w-6xl mx-auto">
          {loading ? <p className="py-20 text-center text-xs tracking-[0.3em] uppercase text-black/30">Loading…</p>
            : shown.length === 0 ? <p className="py-20 text-center text-xs tracking-[0.3em] uppercase text-black/30" data-testid="vendors-empty">No vendors listed yet</p>
            : (
              <Stagger key={category} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
                {shown.map((v) => <StaggerItem key={v.id}><VendorCard vendor={v} /></StaggerItem>)}
              </Stagger>
            )}
        </div>
      </section>

      <section className="bg-black text-white py-24 px-6 text-center">
        <Reveal className="max-w-2xl mx-auto">
          <h2 className="font-presti text-3xl sm:text-4xl md:text-5xl leading-[1.1] mb-5">Are you a vendor we should know?</h2>
          <p className="text-white/50 text-sm md:text-base mb-10">Join our Partners programme — refer couples, get referred back.</p>
          <Magnetic>
            <Link to="/partners" className="inline-flex items-center gap-3 px-10 py-4 bg-white text-black text-xs tracking-[0.25em] uppercase hover:bg-white/90 transition-colors" data-testid="vendors-partners-cta">Partner with us <ArrowUpRight size={14} /></Link>
          </Magnetic>
        </Reveal>
      </section>
    </main>
  );
}
